import Chart from 'highcharts-react-official';
import noData from 'highcharts/modules/no-data-to-display';
import brokenAxis from 'highcharts/modules/broken-axis';
import { memo, useEffect, useMemo, useRef } from 'react';

import Highcharts from 'highcharts';
import moment from 'moment';
import { capitalize, partition } from 'lodash-es';
import {
  AggregatedFreeCashFlow,
  FreeCashFlowAggregationPeriod,
  SlotCategory,
  MeasurementType,
  FreeCashFlowEntry,
} from 'types/evaluation';
import { parseMomentUtc, US_DATE_FORMAT } from 'utils/dates';
import { createYAxisFormatter } from './numberFormatter';
import { useResolveDateRange } from 'pages/packages/show/evaluation-result/aggregated-free-cash-flow/hooks';
// Deep imports (not the barrel) so this chart doesn't drag in the SciChart exporter /
// html-to-image / the options drawer — it only needs the Highcharts exporter + presets.
import { createHighchartsExporter } from 'components/chart-export/highcharts-exporter';
import { GENERIC_CHART_ELEMENTS } from 'components/chart-export/chart-element-presets';
import type { ChartImageExporter } from 'components/chart-export/types';
import { PLOT_LINE_LABEL_ALLOW_OVERLAP } from 'components/charts/plot-line-label';
import { getTickOffset } from './utils';
import {
  SLOT_CATEGORY_COLORS,
  SLOT_CATEGORY_LABELS,
  SLOT_CATEGORY_STACK_ORDER,
  slotCategoryRecord,
} from 'utils/slot-categories';

type Props = {
  packageId: number;
  isLoading: boolean;
  aggregatedFreeCashFlow: AggregatedFreeCashFlow;
  slotCategoryVisibility: Record<SlotCategory, boolean>;
  measurementType: MeasurementType;
  /**
   * Opt-in image-export seam: called with a ready-to-use image exporter for this chart (and
   * `null` on unmount). Absent by default, so the legacy aggregated-free-cash-flow consumer
   * is untouched; eval3-new passes a callback that registers the chart for content export
   * (captured offscreen from its options, so it exports even while the section is hidden).
   */
  onExporterReady?: (exporter: ChartImageExporter | null) => void;
  /**
   * Opt-in date-range override: the resolved `{ minDate, maxDate }` window. Absent by
   * default, so the legacy consumer keeps deriving the window from the per-package
   * `useResolveDateRange` hook; eval3-new passes its own resolved range (from the display
   * settings) so the chart doesn't depend on the legacy per-package atoms.
   */
  resolvedDateRange?: { minDate: moment.Moment; maxDate: moment.Moment };
  /**
   * Opt-in export-only footer lines, rendered as the chart caption in the exported image.
   * eval3-new passes the Cumulative Cash Flow summary so the exported chart is self-contained
   * (those numbers live in a sibling table on screen, outside the chart); absent for the legacy
   * consumer. Only meaningful alongside `onExporterReady`.
   */
  exportFooter?: string[];
};

noData(Highcharts);
brokenAxis(Highcharts);

export const colors: Record<SlotCategory | 'cumulative', string> = {
  ...SLOT_CATEGORY_COLORS,
  cumulative: '#E0E0E0',
};

export const AggregatedFreeCashFlowChart = memo((prop: Props) => {
  const isFreeCashFlowMidInterval = (freeCashFlow: FreeCashFlowEntry) => {
    return parseMomentUtc(freeCashFlow.date).isSame(
      parseMomentUtc(freeCashFlow.date).startOf(
        prop.aggregatedFreeCashFlow.interval
      )
    );
  };

  const [
    [aggregatedFreeCashFlow, midIntervalFreeCashFlow],
    interval,
    effectiveDate,
    isLoading,
    slotCategoryVisibility,
    measurementType,
  ] = [
    partition(prop.aggregatedFreeCashFlow.freeCashflows, (freeCashFlow) =>
      isFreeCashFlowMidInterval(freeCashFlow)
    ),
    prop.aggregatedFreeCashFlow.interval,
    prop.aggregatedFreeCashFlow.effectiveDate,
    prop.isLoading,
    prop.slotCategoryVisibility,
    prop.measurementType,
  ];
  const effectiveDateUTCMoment = parseMomentUtc(effectiveDate);

  const isMidIntervalCashflowNeeded = !effectiveDateUTCMoment
    .clone()
    .isSame(effectiveDateUTCMoment.clone().startOf(interval));

  const midIntervalUTCMoment = effectiveDateUTCMoment
    .clone()
    .startOf(interval)
    .add(1, 'second');

  const chartRef = useRef<{ chart: Highcharts.Chart }>(null);

  // The hook must be called unconditionally (React hook rules) — it computes the legacy
  // per-package fallback window. When the consumer injects `resolvedDateRange` (eval3-new),
  // prefer it; otherwise keep the legacy behavior (the fallback), byte-for-byte.
  const fallbackRange = useResolveDateRange(
    prop.packageId,
    effectiveDate,
    interval,
    isMidIntervalCashflowNeeded
  );
  const { minDate: startDate, maxDate: endDate } =
    prop.resolvedDateRange ?? fallbackRange;

  // Build an image exporter for this chart ONLY when a consumer opts in — the legacy
  // aggregated-free-cash-flow consumer passes no callback, so this stays inert there. The
  // offscreen capture serializes the same series + legend that render on screen.
  const { onExporterReady } = prop;
  const exporter = useMemo<ChartImageExporter | null>(() => {
    if (!onExporterReady) {
      return null;
    }
    return createHighchartsExporter({
      getChart: () => chartRef.current?.chart ?? null,
      elementHooks: { getElements: () => GENERIC_CHART_ELEMENTS },
      // The "Eff Date" plotline is added imperatively (not in the base options), so tell the
      // exporter to copy live plotlines; append the cumulative summary as an export-only footer.
      copyPlotLines: true,
      footerLines: prop.exportFooter,
    });
  }, [onExporterReady, prop.exportFooter]);

  // Push the exporter to the consumer on ready, and null it on unmount (opt-in seam).
  useEffect(() => {
    onExporterReady?.(exporter);
    return () => onExporterReady?.(null);
  }, [onExporterReady, exporter]);

  const cumulativeCashflowData = (() => {
    // Group data by date
    const groupedByDate = aggregatedFreeCashFlow
      .filter(
        (item) =>
          parseMomentUtc(item.date).isSameOrAfter(effectiveDateUTCMoment) &&
          slotCategoryVisibility[item.category]
      )
      .reduce(
        (acc, item) => {
          if (!acc[item.date]) {
            acc[item.date] = [];
          }
          acc[item.date].push(item);
          return acc;
        },
        {} as Record<string, typeof aggregatedFreeCashFlow>
      );

    // Keep track of last known values for each category
    const lastKnownValues: Record<SlotCategory, number> = slotCategoryRecord(
      () => 0
    );

    // Calculate total for each date based on visible categories
    return Object.keys(groupedByDate).map((dateString) => {
      const items = groupedByDate[dateString];

      // Update last known values for each category present in this date
      items.forEach((item) => {
        lastKnownValues[item.category] =
          item[`cumulative${capitalize(measurementType)}Cashflow`];
      });

      // Sum up values from all visible categories, using last known value if not present in current date
      const dateTotal = Object.keys(slotCategoryVisibility).reduce(
        (sum, category) => {
          // Check if this category exists in the current date's data
          const categoryItem = items.find((item) => item.category === category);

          if (categoryItem) {
            // Use current value if available
            return (
              sum +
              categoryItem[`cumulative${capitalize(measurementType)}Cashflow`]
            );
          } else {
            // Use last known value if category is not present in current date
            return sum + lastKnownValues[category as SlotCategory];
          }
        },
        0
      );
      return [parseMomentUtc(dateString).valueOf(), dateTotal];
    });
  })();

  const xAxisGranularity: Record<FreeCashFlowAggregationPeriod, any> = {
    year: {
      tickInterval: 365 * 24 * 3600 * 1000,
      dateTimeLabelFormats: {
        year: '%Y',
      },
    },
    month: {
      tickInterval: 30 * 24 * 3600 * 1000,
      dateTimeLabelFormats: {
        month: '%b',
      },
    },
    quarter: {
      tickInterval: 3 * 30 * 24 * 3600 * 1000,
      dateTimeLabelFormats: {
        month: '%b',
      },
    },
  };

  const xAxisLabelFormatter = (value: number) => {
    if (interval === 'year') {
      return Highcharts.dateFormat('%Y', value);
    } else if (interval === 'quarter') {
      return `Q${Math.floor(parseMomentUtc(value).month() / 3) + 1}`;
    } else if (interval === 'month') {
      return Highcharts.dateFormat('%b', value);
    }
    return '';
  };

  const barTooltipRenderer = (
    color: string,
    name: string,
    interval: string,
    value?: number | null,
    date?: number | null,
    point?: Highcharts.Point
  ) => {
    // Calculate total for this x-axis point across all visible series
    let total = 0;
    if (point && point.series.chart) {
      const chart = point.series.chart;
      const x = point.x;

      chart.series.forEach((series) => {
        if (series.visible && series.type === 'column') {
          const pointAtSameX = series.points.find((p) => p.x === x);
          if (pointAtSameX && pointAtSameX.y) {
            total += pointAtSameX.y;
          }
        }
      });
    }

    return `
    <span style="color:${color}">●</span> ${name} : 
    <b>$${value ? Highcharts.numberFormat(value, 0, '.', ',') : '-'}</b><br/>
    <span>Total: <b>$${Highcharts.numberFormat(
      total,
      0,
      '.',
      ','
    )}</b></span><br/>
    <span>${capitalize(interval)}:
    <b>${date ? xAxisLabelFormatter(date) : '-'}</b></span><br/>
    <span>Year: <b>${date ? Highcharts.dateFormat('%Y', date) : '-'}</b></span>
    `;
  };

  const cumulativeTooltipRenderer = (
    color: string,
    interval: string,
    value?: number | null,
    date?: number | null
  ) => {
    return `
              <span><span style="color:${color}">● </span><b>${
                date ? xAxisLabelFormatter(date) : '-'
              }</span>
              <span>${
                interval !== 'year'
                  ? date
                    ? Highcharts.dateFormat('%Y', date)
                    : '-'
                  : ''
              }</span></b>
              <br/>
              <span>Cumulative Cash Flow: <b>$${
                value ? Highcharts.numberFormat(value, 0, '.', ',') : '-'
              }</b></span>
            `;
  };

  useEffect(() => {
    if (!chartRef.current) return;
    if (isLoading) {
      chartRef.current.chart.xAxis[0].update({
        visible: false,
      });
      chartRef.current.chart.showLoading();
      // @ts-expect-error - Highcharts type definitions are missing hideNoData method
      chartRef.current.chart.hideNoData();
    } else {
      chartRef.current.chart.hideLoading();
      chartRef.current.chart.xAxis[0].update({
        visible: true,
      });
      if (aggregatedFreeCashFlow.length === 0) {
        // @ts-expect-error - Highcharts type definitions are missing hideNoData method
        chartRef.current.chart.showNoData();
      }
    }
  }, [isLoading, aggregatedFreeCashFlow]);

  // Phase 2: Enable stack labels after the chart has painted.
  // Data labels are replaced by tooltips; only stack totals are deferred.
  useEffect(() => {
    if (!chartRef.current?.chart || isLoading || interval !== 'year') return;
    const chart = chartRef.current.chart;
    let timer: ReturnType<typeof setTimeout>;
    const rafId = requestAnimationFrame(() => {
      timer = setTimeout(() => {
        chart.update(
          {
            yAxis: [{ stackLabels: { enabled: true } }, {}],
          },
          true
        );
      }, 0);
    });
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timer);
    };
  }, [isLoading, interval, aggregatedFreeCashFlow]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (chart && !isLoading) {
      if (isMidIntervalCashflowNeeded) {
        SLOT_CATEGORY_STACK_ORDER.forEach((category, index) => {
          chartRef.current?.chart.addSeries({
            id: `mid-interval-series-${index}`,
            visible: slotCategoryVisibility[category],
            type: 'column',
            name: `${SLOT_CATEGORY_LABELS[category]} Revenue`,
            color: colors[category],
            data: midIntervalFreeCashFlow
              .filter((item) => item.category === category)
              .map((item) => [
                midIntervalUTCMoment.valueOf(),
                item[`${measurementType}Cashflow`],
              ]),
          });
        });

        const midIntervalTotal = midIntervalFreeCashFlow.reduce(
          (sum, item) =>
            sum +
            (slotCategoryVisibility[item.category]
              ? item[`cumulative${capitalize(measurementType)}Cashflow`]
              : 0),
          0
        );

        // Add mid-interval point to cumulative series
        const cumulativeSeries = chart.series.find(
          (series) => series.name === 'Cumulative Cash Flow'
        );
        if (cumulativeSeries && midIntervalTotal > 0) {
          cumulativeSeries.addPoint(
            [midIntervalUTCMoment.valueOf(), midIntervalTotal],
            false
          );
        }

        chart.xAxis[0].update(
          {
            labels: {
              formatter: function (
                this: Highcharts.AxisLabelsFormatterContextObject
              ) {
                return xAxisLabelFormatter(Number(this.value));
              },
            },
            tickPositioner: function () {
              const positions = this.tickPositions || [];
              if (!positions.includes(midIntervalUTCMoment.valueOf())) {
                positions.push(midIntervalUTCMoment.valueOf());
                positions.sort((a, b) => a - b);
              }
              return positions;
            },
          },
          false
        );
        chart.redraw();
      }

      const tickOffset = getTickOffset(
        chart.xAxis[0],
        chart.plotWidth,
        isMidIntervalCashflowNeeded
      );

      chart.xAxis[0].addPlotLine({
        value: midIntervalUTCMoment.clone().add(2, 'seconds').valueOf(),
        id: 'mid-interval-plot-line',
        // Declared color (was transparent + a live SVG stroke below) so the plotline's
        // OPTIONS carry the real color — the image exporter copies plotline options, so a
        // transparent option exported an invisible line. The SVG `.attr` below still sets the
        // same stroke on-screen, so the live chart is unchanged.
        color: '#FFD000',
        width: 1.5,
        zIndex: 1,
        label: {
          ...PLOT_LINE_LABEL_ALLOW_OVERLAP,
          text: `Eff Date ${effectiveDateUTCMoment.format(US_DATE_FORMAT)}`,
          align: 'center',
          verticalAlign: 'top',
          y: -10,
          rotation: 0,
        },
      });

      // @ts-expect-error - Highcharts type definitions are missing plotLinesAndBands property
      const plotLines = chart.xAxis[0].plotLinesAndBands;
      const midIntervalPlotLine = plotLines.find(
        (line: any) => line.id === 'mid-interval-plot-line'
      );
      if (midIntervalPlotLine && isFinite(tickOffset)) {
        midIntervalPlotLine?.svgElem?.attr({
          transform: `translate(-${tickOffset}, 0)`,
          stroke: '#FFD000',
        });
      }

      chart.redraw();

      return () => {
        // React 18 can run this cleanup after HighchartsReact has already
        // destroyed the chart on unmount; touching a destroyed chart throws in
        // redraw() (reads this.legend of undefined). Bail if the chart is gone.
        if (!chart?.renderer) return;
        chart.xAxis?.[0]?.removePlotLine('mid-interval-plot-line');
        chart.series?.forEach((series, index) => {
          const midIntervalSeries = chart.get(`mid-interval-series-${index}`);
          if (midIntervalSeries) {
            midIntervalSeries.remove(false);
          }
        });
        chart.redraw();
      };
    }
    return undefined;
  });

  useEffect(() => {
    if (!chartRef.current?.chart) return;
    const chart = chartRef.current.chart;
    const yAxisExtremes = chart.yAxis.map((axis) => axis.getExtremes());
    const hasNegativeValues = yAxisExtremes.reduce(
      (acc, { dataMin }) => acc || (dataMin !== null && dataMin < 0),
      false
    );

    chart.yAxis.forEach((axis) => {
      const extremes = axis.getExtremes();
      const { dataMax } = extremes;

      if (hasNegativeValues) {
        axis.setExtremes(-dataMax, undefined, false);
      }
    });
    chart.redraw();
  });

  return (
    <Chart
      ref={chartRef}
      highcharts={Highcharts}
      options={{
        credits: {
          enabled: false,
        },
        // Importing the Highcharts image-exporter (for the opt-in content-export seam)
        // registers the `exporting` module, which otherwise renders its default context
        // (hamburger) button. Disable that button so this chart looks exactly as before on
        // every consumer (legacy + eval3-new) — the export API stays available (offscreen
        // capture uses it directly, not this button).
        exporting: {
          buttons: {
            contextButton: {
              enabled: false,
            },
          },
        },
        chart: {
          marginRight: 100,
          marginLeft: 100,
        },
        noData: {
          position: {
            align: 'center',
            verticalAlign: 'middle',
          },
        },
        lang: {
          noData: 'No data to display',
        },
        tooltip: {
          headerFormat: '',
          formatter: function (this: Highcharts.TooltipFormatterContextObject) {
            if (this.series.type === 'column') {
              return barTooltipRenderer(
                this.point.color as string,
                this.series.name,
                interval,
                this.y,
                this.x as number,
                this.point
              );
            } else if (this.series.type === 'spline') {
              return cumulativeTooltipRenderer(
                this.color as string,
                interval,
                this.y,
                this.x as number
              );
            } else {
              return false;
            }
          },
        },
        plotOptions: {
          column: {
            stacking: 'normal',
          },
          spline: {
            marker: {
              enabled: false,
              radius: 1,
              states: {
                hover: {
                  enabled: true,
                  radiusPlus: 3,
                },
              },
            },
            dataLabels: {
              enabled: false,
            },
          },
          series: {
            states: {
              hover: {
                enabled: true,
                halo: {
                  size: 0,
                },
              },
              inactive: {
                opacity: 1,
              },
            },
            dataLabels: {
              enabled: false,
              useHTML: true,
              formatter: function (this: Highcharts.PointLabelObject) {
                return this.y === 0
                  ? null
                  : `$${Highcharts.numberFormat(this.y ?? 0, 0, '.', ',')}`;
              },
            },
          },
        },
        legend: {
          enabled: false,
        },
        title: {
          text: 'Enhanced Free Cash Flow',
          align: 'center',
          y: 5,
        },
        xAxis: [
          {
            min: startDate.valueOf(),
            max: endDate.startOf(interval).add(1, 'day').valueOf(),
            type: 'datetime',
            tickLength: 0,
            tickInterval: xAxisGranularity[interval]?.tickInterval,
            labels: {
              useHTML: true,
              formatter: function (
                this: Highcharts.AxisLabelsFormatterContextObject
              ) {
                return xAxisLabelFormatter(Number(this.value));
              },
              y: 15,
            },
            breaks: isMidIntervalCashflowNeeded
              ? [
                  {
                    from: midIntervalUTCMoment.valueOf(),
                    to: midIntervalUTCMoment.valueOf(),
                    breakSize:
                      interval === 'year'
                        ? 365 * 24 * 3600 * 1000
                        : 91.25 * 24 * 3600 * 1000,
                  },
                ]
              : [],
            ordinal: false,
          },
          {
            type: 'datetime',
            tickLength: 5,
            tickWidth: 1,
            opposite: false,
            linkedTo: 0,
            visible: interval !== 'year',
            labels: {
              useHTML: true,
              formatter: function (
                this: Highcharts.AxisLabelsFormatterContextObject
              ) {
                return Highcharts.dateFormat('%Y', Number(this.value));
              },
            },
            tickPositioner: function (
              this: Highcharts.AxisLabelsFormatterContextObject
            ) {
              const chart = this.chart;
              const xMin =
                chart.xAxis[0].min || chart.xAxis[0].getExtremes().min || 0;
              const xMax =
                chart.xAxis[0].max || chart.xAxis[0].getExtremes().max || 0;
              const startYear = new Date(xMin).getFullYear();
              const endYear = new Date(xMax).getFullYear();

              const positions = [];
              for (let year = startYear; year <= endYear; year++) {
                positions.push(Date.UTC(year, 0, 1));
              }
              return positions;
            },
          },
        ],
        yAxis: [
          {
            stackLabels: {
              enabled: false,
              useHTML: true,
              align: 'center',
              formatter: function (this: Highcharts.StackItemObject) {
                return `$${Highcharts.numberFormat(
                  this.total ?? 0,
                  0,
                  '.',
                  ','
                )}`;
              },
            },
            title: {
              text: `${capitalize(`${interval}ly`)} CCF ($)`,
              style: {
                fontSize: '12px',
                fontWeight: 'bold',
              },
            },
            labels: {
              useHTML: true,
              formatter: createYAxisFormatter({ decimals: 0 }),
            },
          },
          {
            title: {
              text: 'Cumulative Cash Flow ($)',
              style: {
                fontSize: '12px',
                fontWeight: 'bold',
              },
            },
            labels: {
              useHTML: true,
              formatter: createYAxisFormatter({ decimals: 0 }),
            },
            opposite: true,
          },
        ],
        series: [
          // One column series per category, LTD on top and PDP at the base. A row whose category
          // is not in the shared list (an old eval's grouped bucket) has no series.
          ...SLOT_CATEGORY_STACK_ORDER.map((category) => ({
            type: 'column' as const,
            visible: slotCategoryVisibility[category],
            name: `${SLOT_CATEGORY_LABELS[category]} Revenue`,
            data: aggregatedFreeCashFlow
              .filter((item) => item.category === category)
              .map((item) => [
                parseMomentUtc(item.date).valueOf(),
                item[`${measurementType}Cashflow`],
              ]),
            color: colors[category],
          })),
          {
            type: 'spline',
            visible: true,
            stickyTracking: false,
            name: 'Cumulative Cash Flow',
            data: cumulativeCashflowData,
            yAxis: 1,
            color: colors.cumulative,
            lineWidth: 3,
            zIndex: 5,
          },
        ],
      }}
    ></Chart>
  );
});
AggregatedFreeCashFlowChart.displayName = 'AggregatedFreeCashFlowChart';
